import React, { useEffect, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { ArrowRightIcon, PhoneIcon, GridIcon, ListIcon, ChevronDownIcon } from 'lucide-react';
import { SelectedProduct } from '../App';
import { products } from '../components/ProductsSection';
interface ProductsPageProps {
  onNavigate: (page: string) => void;
  onRentProduct: (product: SelectedProduct) => void;
}
export function ProductsPage({
  onNavigate,
  onRentProduct
}: ProductsPageProps) {
  const [sortBy, setSortBy] = useState('popularity');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isSortOpen, setIsSortOpen] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const sortOptions = [{
    value: 'popularity',
    label: 'Sort by popularity'
  }, {
    value: 'price-low',
    label: 'Sort by price: low to high'
  }, {
    value: 'price-high',
    label: 'Sort by price: high to low'
  }, {
    value: 'name',
    label: 'Sort by name'
  }];
  const sortedProducts = [...products].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'name':
        return a.name.localeCompare(b.name);
      default:
        return 0;
    }
  });
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  return <main className="flex-grow w-full bg-brand-gray" data-id="element-1269">
      <PageHeader title="Our Products" breadcrumb="Products" onNavigateHome={() => onNavigate('home')} data-id="element-1270" />

      <section className="py-16 md:py-24" data-id="element-1271">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1272">
          <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-1273">
            <p className="text-lg text-brand-charcoal/70 leading-relaxed" data-id="element-1274">
              Browse our full range of mobility equipment available for rent.
              All rentals include free delivery and pickup. 3 days minimum for
              all rentals.
            </p>
          </div>

          {/* Toolbar */}
          <div className="flex items-center justify-between mb-8 bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-3" data-id="element-1275">
            {/* Sort By */}
            <div className="flex items-center gap-3" data-id="element-1276">
              <span className="text-sm font-semibold text-brand-charcoal/60 hidden sm:inline" data-id="element-1277">
                Sort by:
              </span>
              <div className="relative" data-id="element-1278">
                <button onClick={() => setIsSortOpen(!isSortOpen)} className="flex items-center gap-2 border border-gray-200 rounded-lg px-4 py-2 text-sm font-medium text-brand-navy bg-white hover:border-gray-300 transition-colors cursor-pointer min-w-[200px] justify-between" data-id="element-1279">
                  {sortOptions.find(o => o.value === sortBy)?.label}
                  <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} data-id="element-1280" />
                </button>
                {isSortOpen && <>
                    <div className="fixed inset-0 z-10" onClick={() => setIsSortOpen(false)} data-id="element-1281" />
                    <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl border border-gray-100 py-1 min-w-[220px] z-20" data-id="element-1282">
                      {sortOptions.map(option => <button key={option.value} onClick={() => {
                    setSortBy(option.value);
                    setIsSortOpen(false);
                  }} className={`w-full text-left px-4 py-2.5 text-sm transition-colors cursor-pointer ${sortBy === option.value ? 'text-brand-red bg-brand-red/5 font-semibold' : 'text-brand-charcoal hover:bg-gray-50'}`} data-id="element-1283">
                          {option.label}
                        </button>)}
                    </div>
                  </>}
              </div>
            </div>

            {/* View Toggle */}
            <div className="flex items-center gap-3" data-id="element-1284">
              <span className="text-sm font-semibold text-brand-charcoal/60 hidden sm:inline" data-id="element-1285">
                View:
              </span>
              <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden" data-id="element-1286">
                <button onClick={() => setViewMode('grid')} className={`p-2 transition-colors cursor-pointer ${viewMode === 'grid' ? 'bg-brand-red text-white' : 'bg-white text-brand-charcoal/50 hover:bg-gray-50'}`} aria-label="Grid view" data-id="element-1287">
                  <GridIcon className="w-4 h-4" data-id="element-1288" />
                </button>
                <button onClick={() => setViewMode('list')} className={`p-2 transition-colors cursor-pointer ${viewMode === 'list' ? 'bg-brand-red text-white' : 'bg-white text-brand-charcoal/50 hover:bg-gray-50'}`} aria-label="List view" data-id="element-1289">
                  <ListIcon className="w-4 h-4" data-id="element-1290" />
                </button>
              </div>
            </div>
          </div>

          {/* Products — Grid View */}
          {viewMode === 'grid' && <motion.div variants={containerVariants} initial="hidden" animate="visible" key={`grid-${sortBy}`} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1291">
              {sortedProducts.map((product, index) => <motion.div key={product.name} variants={cardVariants} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group flex flex-col h-full" data-id="element-1292">
                  <div className="bg-gray-50 p-6 flex items-center justify-center aspect-square relative overflow-hidden" data-id="element-1293">
                    <img src={product.image} alt={product.name} className="w-full h-full object-contain mix-blend-multiply group-hover:scale-110 transition-transform duration-500" data-id="element-1294" />
                  </div>
                  <div className="p-6 flex flex-col flex-grow" data-id="element-1295">
                    <h3 className="text-lg font-bold text-brand-navy mb-4 flex-grow" data-id="element-1296">
                      {product.name}
                    </h3>
                    <div className="flex items-baseline mb-6" data-id="element-1297">
                      <span className="text-sm text-brand-charcoal/60 mr-1" data-id="element-1298">
                        From
                      </span>
                      <span className="text-2xl font-extrabold text-brand-red" data-id="element-1299">
                        ${product.price.toFixed(2)}
                      </span>
                      <span className="text-sm text-brand-charcoal/60 ml-1" data-id="element-1300">
                        /day
                      </span>
                    </div>
                    <button onClick={() => onRentProduct(product)} className="w-full py-3 bg-brand-navy/5 text-brand-navy rounded-xl font-bold text-center hover:bg-brand-red hover:text-white transition-colors flex items-center justify-center gap-2 group/btn cursor-pointer" data-id="element-1301">
                      Rent Now
                      <ArrowRightIcon className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" data-id="element-1302" />
                    </button>
                  </div>
                </motion.div>)}
            </motion.div>}

          {/* Products — List View */}
          {viewMode === 'list' && <motion.div variants={containerVariants} initial="hidden" animate="visible" key={`list-${sortBy}`} className="space-y-4" data-id="element-1303">
              {sortedProducts.map(product => <motion.div key={product.name} variants={cardVariants} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 flex flex-col sm:flex-row items-center" data-id="element-1304">
                  <div className="bg-gray-50 p-4 flex items-center justify-center w-full sm:w-40 h-40 shrink-0" data-id="element-1305">
                    <img src={product.image} alt={product.name} className="w-full h-full object-contain mix-blend-multiply" data-id="element-1306" />
                  </div>
                  <div className="flex-grow p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 w-full" data-id="element-1307">
                    <div data-id="element-1308">
                      <h3 className="text-lg font-bold text-brand-navy mb-1" data-id="element-1309">
                        {product.name}
                      </h3>
                      <div className="flex items-baseline" data-id="element-1310">
                        <span className="text-sm text-brand-charcoal/60 mr-1" data-id="element-1311">
                          From
                        </span>
                        <span className="text-xl font-extrabold text-brand-red" data-id="element-1312">
                          ${product.price.toFixed(2)}
                        </span>
                        <span className="text-sm text-brand-charcoal/60 ml-1" data-id="element-1313">
                          /day
                        </span>
                      </div>
                    </div>
                    <button onClick={() => onRentProduct(product)} className="px-6 py-3 bg-brand-navy/5 text-brand-navy rounded-xl font-bold hover:bg-brand-red hover:text-white transition-colors flex items-center justify-center gap-2 group/btn cursor-pointer shrink-0" data-id="element-1314">
                      Rent Now
                      <ArrowRightIcon className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" data-id="element-1315" />
                    </button>
                  </div>
                </motion.div>)}
            </motion.div>}

          {/* Bottom CTA */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="mt-20 bg-brand-navy rounded-2xl p-8 md:p-12 text-center relative overflow-hidden" data-id="element-1316">
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-brand-red/10 rounded-full blur-3xl pointer-events-none" data-id="element-1317"></div>
            <div className="relative z-10" data-id="element-1318">
              <h3 className="text-2xl font-bold text-white mb-3" data-id="element-1319">
                Need help choosing the right equipment?
              </h3>
              <p className="text-white/60 mb-8 max-w-lg mx-auto" data-id="element-1320">
                Our team will help you select the perfect mobility solution for
                your needs. Call us for personalized recommendations.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1321">
                <a href="tel:+18003954459" className="inline-flex items-center gap-2 px-8 py-3 bg-brand-red text-white rounded-xl font-bold hover:bg-red-700 transition-colors shadow-md" data-id="element-1322">
                  <PhoneIcon className="w-4 h-4" data-id="element-1323" />1 (800) 395-4459
                </a>
                <button onClick={() => onNavigate('contact')} className="inline-flex items-center gap-2 px-8 py-3 bg-white/10 text-white border border-white/20 rounded-xl font-bold hover:bg-white/20 transition-colors" data-id="element-1324">
                  Contact Us
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </main>;
}